import type { ComponentProps, ReactNode } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

import { TableCell, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

/**
 * Keeps the dragged row inside the list: no sideways movement, and never past
 * the first or last row — otherwise the table's wrapper grows a scrollbar.
 */
const restrictToList: Modifier = ({ transform, draggingNodeRect, containerNodeRect }) => {
  if (!draggingNodeRect || !containerNodeRect) return { ...transform, x: 0 }
  const minY = containerNodeRect.top - draggingNodeRect.top
  const maxY = containerNodeRect.bottom - draggingNodeRect.bottom
  return { ...transform, x: 0, y: Math.min(Math.max(transform.y, minY), maxY) }
}

interface SortableTableRowsProps {
  /** Row ids in their current order; each `SortableTableRow` uses one. */
  ids: string[]
  /** The ids in their new order after a row is dropped. */
  onReorder: (ids: string[]) => void
  disabled?: boolean
  children: ReactNode
}

/**
 * Makes the `SortableTableRow`s inside it reorderable by dragging their grip,
 * or with the keyboard. Renders no markup, so it goes inside a `TableBody`.
 */
export function SortableTableRows({ ids, onReorder, disabled, children }: SortableTableRowsProps) {
  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so clicks on the handle stay clicks.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    onReorder(arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))))
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToList]}
      autoScroll={false}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy} disabled={disabled}>
        {children}
      </SortableContext>
    </DndContext>
  )
}

/** A table row dragged by the grip in its first cell. */
export function SortableTableRow({
  id,
  handleLabel,
  className,
  children,
  ...props
}: ComponentProps<typeof TableRow> & {
  id: string
  handleLabel: string
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id })
  return (
    <TableRow
      {...props}
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(className, isDragging && 'relative z-10 bg-(--ad-surface) shadow-md')}
    >
      <TableCell className="w-0 pr-0">
        <button
          type="button"
          ref={setActivatorNodeRef}
          aria-label={handleLabel}
          className="flex cursor-grab touch-none items-center text-(--ad-text-faint) hover:text-(--ad-text) active:cursor-grabbing"
          {...attributes}
          {...listeners}
          // A click on the grip is not a click on the row.
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="size-4" />
        </button>
      </TableCell>
      {children}
    </TableRow>
  )
}
